// Сквозной территориальный фильтр дашборда (globalFilters.scope).
//
// Дашборд собран вокруг одной территории — федерального округа или субъекта.
// Фильтр выбирает другую территорию того же уровня, и запросы всех виджетов
// переписываются на неё: сама территория → выбранная, субъекты округа →
// субъекты выбранного округа, муниципалитеты субъекта → муниципалитеты
// выбранного. Территории считает бэкенд (POST /data/rescope; аноним получает
// данные сохранённого дашборда с scope_territory_id). Выбор — состояние
// просмотра: в определение не пишется, дашборд не становится изменённым.
// У дашборда без scope кнопки «Фильтры» нет.

import type { DashboardScope, Territory, WidgetDefinition, WidgetQuery } from '../types';
import { api } from '../api';
import { tr } from '../ui-i18n';

import { controls, element } from './controls';
import { currentCollator, currentLocale } from './locale';
import { escapeHtml } from './dom';
import { errorMessage, setStatus } from './status';
import { state } from './state';
import { renderWidget } from './widgets';
import { syncFilterbarVisibility } from './workspace';

type ScopeSelection = { dashboardKey: string; territoryId: string };

let selection: ScopeSelection | null = null;
let options: { key: string; territories: Territory[] } | null = null;
let optionsRequest = 0;
const rescoped = new Map<string, Promise<string[]>>();

function dashboardKey(): string {
  return state.currentTemplateId ?? state.currentDashboardId ?? '';
}

export function dashboardScope(): DashboardScope | null {
  return state.definition.globalFilters.scope ?? null;
}

/** Выбранная территория фильтра, если она не совпадает с территорией охвата. */
export function activeScopeTerritoryId(): string | null {
  const scope = dashboardScope();
  if (!scope || !selection || selection.dashboardKey !== dashboardKey()) return null;
  return selection.territoryId === scope.territoryId ? null : selection.territoryId;
}

/** Запрос виджета с территориями выбранной территории фильтра. */
export async function scopedQuery(widget: WidgetDefinition): Promise<WidgetQuery> {
  const scope = dashboardScope();
  const target = activeScopeTerritoryId();
  if (!scope || !target) return widget.query;
  const key = JSON.stringify([scope.level, scope.territoryId, target, widget.query.indicatorId, widget.query.territoryIds]);
  let pending = rescoped.get(key);
  if (!pending) {
    pending = api.rescopeTerritories(scope, target, [
      { indicatorId: widget.query.indicatorId, territoryIds: widget.query.territoryIds },
    ]).then((response) => response.results[0]?.territory_ids ?? []);
    pending.catch(() => rescoped.delete(key));
    rescoped.set(key, pending);
  }
  const territoryIds = await pending;
  if (widget.query.territoryIds.length && !territoryIds.length) {
    throw new Error(tr('Для выбранной территории у показателя виджета нет данных'));
  }
  return { ...widget.query, territoryIds };
}

function levelLabel(scope: DashboardScope): string {
  return scope.level === 'federal_district' ? tr('Федеральный округ') : tr('Субъект РФ');
}

async function loadOptions(scope: DashboardScope): Promise<Territory[]> {
  const key = `${dashboardKey()}\n${scope.level}\n${currentLocale()}`;
  if (options?.key === key) return options.territories;
  const savedId = dashboardKey();
  let territories: Territory[];
  if (savedId) {
    territories = (await api.dashboardScopeOptions(savedId, currentLocale())).territories;
  } else {
    // Несохранённый дашборд (только у вошедшего): каталог справочника ГИС.
    const levels = scope.level === 'region' ? ['federal_district', 'region'] : ['federal_district'];
    const groups = await Promise.all(levels.map((level) => api.territories({ sourceType: 'emiss', level, limit: 500 }, currentLocale())));
    territories = groups.flat()
      .filter((item) => item.id.startsWith('ru_federal_district:') || item.id.startsWith('ru_region:'))
      .map((item) => ({ ...item, selectable: item.level === scope.level }));
  }
  options = { key, territories };
  return territories;
}

function optionsMarkup(scope: DashboardScope, territories: Territory[]): string {
  const option = (item: Territory) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.name)}</option>`;
  const byName = (a: Territory, b: Territory) => currentCollator().compare(a.name, b.name);
  const selectable = territories.filter((item) => item.level === scope.level && item.selectable !== false).sort(byName);
  if (scope.level !== 'region') return selectable.map(option).join('');
  // Субъекты — группами по округам; без округа — в конце.
  const districts = territories.filter((item) => item.level === 'federal_district').sort(byName);
  const grouped = districts
    .map((district) => {
      const regions = selectable.filter((item) => item.federal_district_id === district.id);
      return regions.length ? `<optgroup label="${escapeHtml(district.name)}">${regions.map(option).join('')}</optgroup>` : '';
    })
    .join('');
  const known = new Set(districts.map((item) => item.id));
  const rest = selectable.filter((item) => !item.federal_district_id || !known.has(item.federal_district_id));
  return grouped + rest.map(option).join('');
}

/** Кнопка и панель фильтра под текущий дашборд. */
export function refreshScopeFilterControls(): void {
  const scope = dashboardScope();
  controls.toggleFilterbar.hidden = !scope;
  if (!scope) {
    state.filtersOpen = false;
    syncFilterbarVisibility();
    return;
  }
  syncFilterbarVisibility();
  controls.scopeFilterLabel.textContent = levelLabel(scope);
  const current = activeScopeTerritoryId() ?? scope.territoryId;
  const token = ++optionsRequest;
  void loadOptions(scope)
    .then((territories) => {
      if (token !== optionsRequest) return;
      controls.scopeFilter.innerHTML = optionsMarkup(scope, territories);
      controls.scopeFilter.value = current;
      controls.scopeFilter.disabled = false;
      element<HTMLButtonElement>('#reset-scope-filter').disabled = current === scope.territoryId;
    })
    .catch((error) => {
      if (token !== optionsRequest) return;
      controls.scopeFilter.innerHTML = '';
      controls.scopeFilter.disabled = true;
      setStatus(tr('Не удалось загрузить территории фильтра: {0}', errorMessage(error)), 'error');
    });
  const name = options?.territories.find((item) => item.id === current)?.name;
  controls.scopeFilterNote.textContent = activeScopeTerritoryId() && name
    ? tr('Все виджеты показывают: {0}.', name)
    : tr('Выберите территорию — запросы всех виджетов перестроятся под неё.');
}

export async function applyScopeFilter(territoryId: string): Promise<void> {
  const scope = dashboardScope();
  if (!scope || !territoryId) return;
  selection = { dashboardKey: dashboardKey(), territoryId };
  refreshScopeFilterControls();
  const name = options?.territories.find((item) => item.id === territoryId)?.name ?? territoryId;
  setStatus(tr('Территория дашборда: {0}', name), 'success');
  await Promise.all(state.definition.widgets.map((widget) => renderWidget(widget.id)));
}
